import { useState, useEffect } from "react";
import { T } from "@/components/T";
import { useSettings } from "@/context/SettingsContext";
import { createDonationOrder, verifyDonation, fetchDonors } from "@/lib/api";
import Seo from "@/components/Seo";
import { MandalaMotif, Divider } from "@/components/MotifBackground";
import { Heart, CheckCircle2, HandHeart, BookOpen, Server, Users } from "lucide-react";
import { toast } from "sonner";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const USES = [
  { icon: Server, title: "Keep it running", text: "Servers, storage and the free, ad-light experience for everyone." },
  { icon: BookOpen, title: "Add authentic content", text: "Sourcing and presenting more texts, translations and audio." },
  { icon: HandHeart, title: "Sustain the seva", text: "Supporting the careful, respectful curation of the tradition." },
];

export default function Support() {
  const { donations_enabled, donation_presets, org } = useSettings();
  const [amount, setAmount] = useState(donation_presets[1] || 501);
  const [custom, setCustom] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [donorData, setDonorData] = useState(null);
  useEffect(() => { fetchDonors().then(setDonorData).catch(() => {}); }, []);
  const [donor, setDonor] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const effAmount = custom ? parseInt(custom, 10) : amount;

  const donate = async () => {
    if (!effAmount || effAmount < 1) { toast.error("Please choose or enter an amount."); return; }
    if (!donor.name.trim()) { toast.error("Please enter your name."); return; }
    if (!/^\S+@\S+\.\S+$/.test(donor.email.trim())) { toast.error("Please enter a valid email address."); return; }
    if (!/^[0-9]{10}$/.test(donor.phone.replace(/\D/g, "").slice(-10))) { toast.error("Please enter a valid 10-digit mobile number."); return; }
    if (!(await loadRazorpay())) { toast.error("Could not load the payment window. Check your connection."); return; }
    setBusy(true);
    try {
      const order = await createDonationOrder({ amount: effAmount, ...donor, recurring });
      const options = {
        key: order.key_id,
        name: org?.name || "Dharmartha",
        description: recurring ? "Monthly Seva Donation" : "Seva Donation",
        image: undefined,
        prefill: { name: donor.name, email: donor.email, contact: donor.phone },
        theme: { color: "#C75000" },
        handler: async (res) => {
          try {
            const v = await verifyDonation(res);
            setDone({ ref: v.ref, amount: v.amount || effAmount, recurring });
          } catch {
            toast.error("We could not confirm the payment. If money was deducted, please contact us.");
          }
        },
      };
      if (recurring) options.subscription_id = order.subscription_id;
      else options.order_id = order.order_id;
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => toast.error("Payment failed or was cancelled."));
      rzp.open();
    } catch (err) {
      toast.error("Donations unavailable", { description: err.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  return (
    <>
      <Seo title="Support Us" description="Support Dharmartha with a donation (seva). Help keep the knowledge traditions of Bharat freely accessible online." path="/support" />
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-16 -top-8 h-80 w-80 text-[var(--gold)] opacity-[0.08]" />
        <div className="relative z-[2] mx-auto max-w-4xl px-5 py-16 md:px-8 md:py-24">
          <p className="overline-label mb-3">सेवा · दान</p>
          <h1 className="font-serif-latin text-4xl text-ink md:text-6xl"><T>Support Dharmartha</T></h1>
          <p className="mt-4 max-w-2xl text-lg leading-relaxed text-ink-2">
            <T>Dharmartha is offered as a seva — a free, respectful digital home for the knowledge traditions of Bharat. Your contribution helps keep it running and growing, for everyone.</T>
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-16 md:px-8">
        <div className="grid gap-6 sm:grid-cols-3">
          {USES.map((u) => (
            <div key={u.title} className="border border-line bg-surface p-6">
              <u.icon className="h-6 w-6 text-saffron" />
              <h3 className="mt-4 font-serif-latin text-lg text-ink">{u.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ink-2">{u.text}</p>
            </div>
          ))}
        </div>

        <Divider className="my-12" />

        {done ? (
          <div data-testid="donation-success" className="mx-auto max-w-lg border border-line bg-surface p-10 text-center">
            <CheckCircle2 className="mx-auto h-14 w-14 text-saffron" />
            <h2 className="mt-4 font-serif-latin text-2xl text-ink"><T>Thank you for your seva 🙏</T></h2>
            <p className="mt-2 text-ink-2"><T>Your</T> {done.recurring ? "monthly " : ""}<T>donation of ₹</T>{done.amount} <T>has been received.</T></p>
            <p className="mt-4 inline-block border border-line bg-parchment px-4 py-2 text-sm text-ink-3">Reference: <span className="font-mono text-ink">{done.ref}</span></p>
            <p className="mt-4 text-xs text-ink-3"><T>Please keep this reference for your records.</T></p>
          </div>
        ) : (
          <div className="mx-auto max-w-lg border border-line bg-surface p-7 md:p-9" data-testid="donation-form">
            <div className="mb-4 flex gap-2 border border-line p-1 text-sm">
              <button data-testid="donate-onetime" onClick={() => setRecurring(false)} className={`flex-1 py-2 transition-colors ${!recurring ? "bg-[var(--saffron)] text-white" : "text-ink-2"}`}><T>One-time</T></button>
              <button data-testid="donate-monthly" onClick={() => setRecurring(true)} className={`flex-1 py-2 transition-colors ${recurring ? "bg-[var(--saffron)] text-white" : "text-ink-2"}`}><T>Monthly</T></button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {donation_presets.map((p) => (
                <button key={p} data-testid={`preset-${p}`} onClick={() => { setAmount(p); setCustom(""); }} className={`border py-3 text-center transition-colors ${!custom && amount === p ? "border-[var(--saffron)] bg-[rgba(199,80,0,0.06)] text-saffron" : "border-line text-ink-2 hover:border-[var(--ink)]"}`}>
                  ₹{p}
                </button>
              ))}
            </div>
            <div className="mt-3">
              <label className="mb-1.5 block text-sm text-ink-2"><T>Or enter an amount (₹)</T></label>
              <input data-testid="donate-custom" type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom amount" className="w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none" />
            </div>
            <div className="mt-4 grid gap-3">
              <input data-testid="donor-name" required value={donor.name} onChange={(e) => setDonor({ ...donor, name: e.target.value })} placeholder="Your name *" className="w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none" />
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid="donor-email" required type="email" value={donor.email} onChange={(e) => setDonor({ ...donor, email: e.target.value })} placeholder="Email *" className="w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none" />
                <input data-testid="donor-phone" required type="tel" value={donor.phone} onChange={(e) => setDonor({ ...donor, phone: e.target.value })} placeholder="Mobile number *" className="w-full border border-line bg-parchment px-4 py-2.5 text-ink focus:border-saffron focus:outline-none" />
              </div>
              <p className="text-xs text-ink-3"><T>Name, mobile number and email are required for your donation receipt.</T></p>
            </div>
            <button data-testid="donate-submit" disabled className="mt-6 flex w-full cursor-not-allowed items-center justify-center gap-2 bg-[var(--saffron)]/55 py-3.5 font-medium text-white">
              <Heart className="h-4 w-4" /> <T>Coming Soon</T>
            </button>
            <p className="mt-3 text-center text-xs text-ink-3"><T>Our payment gateway isn't ready yet — donations will open here very soon. Thank you for your patience. 🙏</T></p>
          </div>
        )}

        {donorData && donorData.donors?.length > 0 && (
          <div className="mx-auto mt-16 max-w-3xl" data-testid="supporters-wall">
            <Divider />
            <div className="mt-10 text-center">
              <p className="overline-label text-gold flex items-center justify-center gap-2"><Users className="h-4 w-4" /> <T>Our Supporters</T></p>
              <h2 className="mt-2 font-serif-latin text-3xl text-ink"><T>Kind souls who sustain this seva</T></h2>
              <p className="mt-3 text-sm text-ink-2">
                <T>Together,</T> <span className="font-medium text-ink dh-num">{donorData.count}</span> <T>supporters have contributed</T>
                {" "}<span className="font-medium text-saffron dh-num">₹{donorData.total.toLocaleString("en-IN")}</span> <T>towards keeping Dharmartha freely accessible. 🙏</T>
              </p>
            </div>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {donorData.donors.map((d, i) => (
                <div key={i} data-testid={`donor-${i}`} className="flex items-center justify-between gap-3 border border-line bg-surface px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(199,80,0,0.08)] font-serif-latin text-sm text-saffron">
                      {(d.name || "?").trim().charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{d.name}</p>
                      {d.note && <p className="truncate text-xs text-ink-3">{d.note}</p>}
                    </div>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-saffron dh-num">₹{Number(d.amount || 0).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 text-center text-xs text-ink-3"><T>Your name can join this wall — every contribution, big or small, is a blessing.</T></p>
          </div>
        )}
      </section>
    </>
  );
}
